import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { staggerContainer, fadeInUp, scaleIn, displayFont } from './shared';

export function SaveTheDateVisual() {
  return (
    <div className="flex items-center justify-center p-6 lg:p-10">
      <div className="relative w-full max-w-sm space-y-4">
        {/* Original card */}
        <div className="relative">
          <div className="absolute inset-0 translate-x-3 translate-y-3 rounded-2xl bg-primary/10" />
          <div className="relative rounded-2xl bg-background-card border border-border p-8 sm:p-10 text-center shadow-lg">
            <div className="text-xs font-medium tracking-[0.2em] uppercase text-primary mb-4">
              Save the Date
            </div>
            <h3 className="text-3xl sm:text-4xl text-text-primary mb-2" style={displayFont}>
              Emma &amp; James
            </h3>
            <div className="w-12 h-px bg-primary/40 mx-auto my-4" />
            <p className="text-text-secondary text-sm mb-1">Saturday, 14th June 2026</p>
            <p className="text-text-secondary text-sm">The Cotswolds, Oxfordshire</p>
            <div className="mt-6 inline-flex items-center gap-1.5 text-xs text-text-secondary border border-border rounded-full px-3 py-1.5">
              <Check className="w-3 h-3 text-success" />
              Formal invitation to follow
            </div>
          </div>
        </div>

        {/* Exact replica of the Type field from SettingsEditor */}
        <div className="rounded-xl bg-background-card border border-border px-3 py-3">
          <label className="text-xs font-medium text-text-secondary block mb-1.5">Type</label>
          <div className="flex items-center gap-1 bg-background-elevated rounded-lg p-1">
            <div className="flex-1 text-sm font-medium py-1.5 rounded-md bg-primary text-white shadow-sm text-center">
              Save the Date
            </div>
            <div className="flex-1 text-sm font-medium py-1.5 rounded-md text-text-secondary text-center">
              Invite
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SaveTheDateSection() {
  return (
    <section id="save-the-date" className="py-20 md:py-32 bg-background overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Visual */}
          <motion.div
            variants={scaleIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
          >
            <SaveTheDateVisual />
          </motion.div>

          {/* Text */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
          >
            <motion.span
              variants={fadeInUp}
              className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4"
            >
              Save the Date
            </motion.span>
            <motion.h2
              variants={fadeInUp}
              className="text-3xl md:text-4xl lg:text-5xl text-text-primary mb-4"
              style={displayFont}
            >
              Save the Date, <span className="text-primary">included</span>
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-text-secondary text-base md:text-lg mb-6 leading-relaxed">
              Start with a Save the Date — it's a simple toggle in your invite settings. When you're ready to reveal the full details, just switch it to your full invite. More sections unlock, your design stays exactly the same, and there's nothing more to pay.
            </motion.p>
            <motion.ul variants={fadeInUp} className="space-y-3 mb-8">
              {[
                'Toggle Save the Date on when you create your invite',
                'When ready, flip to your full invite — extra sections become available',
                'Same design, same guests, no repayment required',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-text-secondary">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </motion.ul>
            <motion.div variants={fadeInUp}>
              <Link
                to="/create"
                className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all"
              >
                Create Your Invite <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
